/**
 * Read-only display formatters for the non-monetary units the operator's
 * valuation surfaces render — areas (`m²`) and rates (`%`).
 *
 * Mirrors the customer app's `lib/unit-format.ts` spec exactly (`de-DE`
 * grouping, two fraction digits, the unit after a non-breaking space), because
 * the two apps render the same contract fields and an operator comparing a
 * request against what the agent submitted must read the same string on both.
 *
 * A sibling of `lib/currency.ts` rather than a member of it: the monetary spec
 * is `Intl`'s own `style: 'currency'`, while a unit suffix is ours. Both live
 * in `lib/` so the next operator surface has something to import instead of
 * declaring a per-page `Intl.NumberFormat` (IMM-681).
 *
 * NOT yet the app's only `m²` formatter: `properties.tsx` and
 * `properties/detail.tsx` still declare their own, with a different
 * fraction-digit and spacing spec. Converging them is deliberately out of this
 * bug's scope and is tracked as IMM-687 — new surfaces import from here
 * (BL-022, BL-030).
 */
const NON_BREAKING_SPACE = '\u00A0';

// Two fraction digits, matching the `numeric(_, 2)` columns these values are
// stored in — the formatter must never round stored precision away, which is
// the same defect family as dropping the unit entirely. Trailing zeros are
// dropped, so a whole `82` stays `82` rather than `82,00`.
const STORED_FRACTION_DIGITS = 2;

const areaNumberFormatter = new Intl.NumberFormat('de-DE', {
  useGrouping: true,
  maximumFractionDigits: STORED_FRACTION_DIGITS,
});

const percentNumberFormatter = new Intl.NumberFormat('de-DE', {
  useGrouping: true,
  maximumFractionDigits: STORED_FRACTION_DIGITS,
});

/** Formats an area for display — `82 m²`, `1.250,55 m²`. */
export function formatArea(value: number): string {
  return `${areaNumberFormatter.format(value)}${NON_BREAKING_SPACE}m²`;
}

/** Formats a percentage for display — `85 %`, `92,75 %`. */
export function formatPercent(value: number): string {
  return `${percentNumberFormatter.format(value)}${NON_BREAKING_SPACE}%`;
}
